import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Loader2, Copy, RefreshCw, CheckCircle2, X, Sparkles, ExternalLink, FileText, Trash2, Pencil, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function AdminInfluencers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/influencers`, { headers: auth() });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-influencers">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00F0FF]" />
              Influencer Program
            </h1>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-influencer-btn"
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-[#00F0FF] text-black font-semibold rounded-lg hover:bg-[#00F0FF]/90">
            <Plus className="w-4 h-4" /> Invite influencer
          </button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#00F0FF]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-neutral-500">
            <Sparkles className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-lg text-white font-semibold mb-2">No influencers yet</p>
            <p className="text-sm">Invite your first one to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((inf) => (
              <InfluencerRow key={inf.affiliate_id} inf={inf} onClick={() => setDetail(inf.affiliate_id)} onReload={load} />
            ))}
          </div>
        )}
      </section>

      {showNew && <NewInfluencerModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {detail && <InfluencerDetailModal affiliateId={detail} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function InfluencerRow({ inf, onClick, onReload }) {
  const trackingUrl = `https://theailedger.ai/${inf.slug}`;
  const status = inf.status;
  const statusColor = {
    active: 'bg-emerald-500/10 text-emerald-400',
    invited: 'bg-amber-500/10 text-amber-400',
    deactivated: 'bg-red-500/10 text-red-400',
    deleted: 'bg-neutral-500/10 text-neutral-400',
  }[status] || 'bg-white/5 text-neutral-400';

  return (
    <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-[#00F0FF]/30 transition" data-testid="influencer-row">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-white">{inf.name}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold ${statusColor}`}>{status}</span>
          {inf.primary_platform && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-neutral-400 uppercase">{inf.primary_platform}</span>
          )}
          {!!inf.follower_count && (
            <span className="text-xs text-neutral-500">{(inf.follower_count / 1000).toFixed(0)}K followers</span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">{inf.email} · {inf.commission_rate}% commission</div>
        <code className="text-xs text-[#00F0FF] mt-1 inline-block">{trackingUrl}</code>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <Stat label="Clicks" value={inf.total_clicks ?? 0} />
        <Stat label="Conv." value={inf.total_conversions ?? 0} />
        <Stat label="Owed" value={`$${(inf.total_owed ?? 0).toFixed(0)}`} accent="#f59e0b" />
        <Stat label="Paid" value={`$${(inf.total_paid ?? 0).toFixed(0)}`} accent="#34d399" />
      </div>
      <div className="flex gap-2">
        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(trackingUrl); toast.success('Link copied'); }}
                title="Copy tracking link"
                className="p-2 bg-white/5 hover:bg-white/10 rounded-lg">
          <Copy className="w-4 h-4 text-neutral-400" />
        </button>
        {status === 'active' && (
          <button onClick={async (e) => {
            e.stopPropagation();
            try {
              await axios.post(`${API}/api/admin/influencers/${inf.affiliate_id}/resend`, {}, { headers: auth() });
              toast.success('Dashboard link emailed');
              onReload();
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
          }} title="Send dashboard link via email"
                  data-testid={`resend-${inf.slug}`}
                  className="p-2 bg-white/5 hover:bg-white/10 rounded-lg">
            <RefreshCw className="w-4 h-4 text-neutral-400" />
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = '#ffffff' }) {
  return (
    <div className="text-center min-w-[55px]">
      <div className="text-xs text-neutral-500 uppercase tracking-wide">{label}</div>
      <div className="font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function NewInfluencerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', slug: '', commission_rate: 1.0,
    follower_count: '', primary_platform: 'tiktok', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        slug: form.slug.toLowerCase().trim().replace(/[^a-z0-9_-]/g, ''),
        follower_count: form.follower_count ? parseInt(form.follower_count, 10) : null,
        commission_rate: parseFloat(form.commission_rate),
      };
      const res = await axios.post(`${API}/api/admin/influencers`, payload, { headers: auth() });
      toast.success('Invited — email sent');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-950 border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">Invite influencer</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name *">
            <input required value={form.name} onChange={update('name')} data-testid="new-name"
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white" />
          </Field>
          <Field label="Email *">
            <input required type="email" value={form.email} onChange={update('email')} data-testid="new-email"
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white" />
          </Field>
          <Field label="Custom URL suffix (slug) *">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500 whitespace-nowrap">theailedger.ai/</span>
              <input required value={form.slug} onChange={update('slug')} data-testid="new-slug"
                     placeholder="sarahbuilds"
                     className="flex-1 bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-[#00F0FF] font-mono" />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Lowercase letters, numbers, hyphens, underscores. 2-32 chars.</p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Commission rate (%)">
              <input type="number" step="0.1" min="0" max="50" value={form.commission_rate}
                     onChange={update('commission_rate')}
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white" />
            </Field>
            <Field label="Followers">
              <input type="number" min="0" value={form.follower_count} onChange={update('follower_count')}
                     placeholder="42000"
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white" />
            </Field>
          </div>
          <Field label="Primary platform">
            <select value={form.primary_platform} onChange={update('primary_platform')}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white">
              {['tiktok', 'instagram', 'youtube', 'x', 'linkedin', 'other'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Notes (internal)">
            <textarea rows={2} value={form.notes} onChange={update('notes')}
                      className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
          </Field>
          <button type="submit" disabled={submitting} data-testid="new-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 disabled:opacity-50">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Send invite email
          </button>
        </form>
      </div>
    </div>
  );
}

function InfluencerDetailModal({ affiliateId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [paidVia, setPaidVia] = useState('paypal');
  const [txId, setTxId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/influencers/${affiliateId}`, { headers: auth() });
      setData(res.data);
      setSelected(new Set());
    } catch (err) { toast.error('Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [affiliateId]);

  if (loading) {
    return <Overlay onClose={onClose}><div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#00F0FF]" /></div></Overlay>;
  }
  if (!data) return null;

  const inf = data.affiliate;
  const commissions = data.commissions || [];
  const toggleSel = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const approve = async () => {
    setBusy(true);
    try {
      const body = selected.size === 0 ? {} : { commission_ids: Array.from(selected) };
      const res = await axios.post(`${API}/api/admin/influencers/${affiliateId}/approve`, body, { headers: auth() });
      toast.success(`Approved ${res.data.approved}`);
      load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const markPaid = async () => {
    if (selected.size === 0) { toast.error('Select commissions first'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/admin/influencers/${affiliateId}/mark-paid`, {
        commission_ids: Array.from(selected),
        paid_via: paidVia,
        tx_id: txId || null,
      }, { headers: auth() });
      toast.success(`Marked ${res.data.paid} paid ($${res.data.total_paid_now})`);
      load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">{inf.name}</h2>
            <p className="text-sm text-neutral-500">{inf.email} · {inf.commission_rate}% rate · {inf.status}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-white/[0.02] border border-white/10 rounded-lg p-3 mb-4">
          <code className="text-sm text-[#00F0FF]">https://theailedger.ai/{inf.slug}</code>
        </div>

        <AgreementSection affiliateId={affiliateId} onChange={onChange} />

        <AnalyticsSection affiliateId={affiliateId} />

        <AdminActionsBar affiliateId={affiliateId} inf={inf} onClose={onClose} onChange={onChange} />

        <div className="grid grid-cols-4 gap-2 mb-4">
          <Tile label="Pending" value={data.pending?.count} amount={data.pending?.amount} accent="#f59e0b" />
          <Tile label="Approved" value={data.approved?.count} amount={data.approved?.amount} accent="#3b82f6" />
          <Tile label="Paid" value={data.paid?.count} amount={data.paid?.amount} accent="#34d399" />
          <Tile label="Clicks (30d)" value={data.recent_clicks_30d} accent="#a78bfa" />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={approve} disabled={busy} data-testid="approve-btn"
                  className="px-4 py-2 text-sm bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded-lg hover:bg-blue-500/30">
            Approve {selected.size > 0 ? `(${selected.size})` : 'all pending'}
          </button>
          <select value={paidVia} onChange={(e) => setPaidVia(e.target.value)}
                  className="px-3 py-2 text-sm bg-black/40 border border-white/20 rounded-lg">
            <option value="paypal">PayPal</option>
            <option value="stripe_connect">Stripe Connect</option>
            <option value="manual">Manual</option>
          </select>
          <input value={txId} onChange={(e) => setTxId(e.target.value)} placeholder="Transaction ID (optional)"
                 className="flex-1 min-w-[180px] px-3 py-2 text-sm bg-black/40 border border-white/20 rounded-lg" />
          <button onClick={markPaid} disabled={busy || selected.size === 0} data-testid="markpaid-btn"
                  className="px-4 py-2 text-sm bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-lg hover:bg-emerald-500/30 disabled:opacity-50">
            Mark paid ({selected.size})
          </button>
        </div>

        {commissions.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-8">No commissions yet.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-neutral-400 uppercase text-xs">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-right p-2">Gross</th>
                  <th className="text-right p-2">Comm.</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Tx ID</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.commission_id} className="border-t border-white/10">
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={selected.has(c.commission_id)}
                             onChange={() => toggleSel(c.commission_id)}
                             disabled={c.status === 'paid'}
                             className="accent-[#00F0FF]" />
                    </td>
                    <td className="p-2 text-neutral-400">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="p-2 text-neutral-300 text-xs">{c.source.replace(/_/g, ' ')}</td>
                    <td className="p-2 text-right">${c.gross_amount.toFixed(2)}</td>
                    <td className="p-2 text-right font-semibold">${c.commission_amount.toFixed(2)}</td>
                    <td className="p-2"><StatusPill status={c.status} /></td>
                    <td className="p-2 text-xs text-neutral-500 truncate max-w-[120px]">{c.paid_tx_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Overlay>
  );
}

function Tile({ label, value, amount, accent }) {
  return (
    <div className="bg-neutral-900/40 border border-white/10 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="text-lg font-bold mt-0.5" style={{ color: accent }}>{value ?? 0}</div>
      {amount !== undefined && <div className="text-xs text-neutral-400">${(amount ?? 0).toFixed(2)}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: 'bg-amber-500/10 text-amber-400',
    approved: 'bg-blue-500/10 text-blue-400',
    paid: 'bg-emerald-500/10 text-emerald-400',
    reversed: 'bg-red-500/10 text-red-400',
  };
  return <span className={`text-xs px-2 py-0.5 rounded uppercase tracking-wide ${map[status] || 'bg-white/5 text-neutral-400'}`}>{status}</span>;
}

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-neutral-950 border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function AgreementSection({ affiliateId, onChange }) {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/influencers/${affiliateId}/agreement`, { headers: auth() });
      setAgreements(res.data.items || []);
    } catch (err) {
      // Non-fatal — the affiliate just may not have any agreements yet
      setAgreements([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [affiliateId]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/influencers/${affiliateId}/agreement/generate`,
        {}, { headers: auth() }
      );
      navigator.clipboard.writeText(res.data.sign_url);
      toast.success('Agreement link copied to clipboard');
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally {
      setBusy(false);
    }
  };

  const signed = agreements.find((a) => a.status === 'signed');
  const unsignedLatest = agreements.find((a) => a.status === 'unsigned');

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4 mb-4" data-testid="agreement-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold inline-flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#00F0FF]" /> Founding Affiliate Agreement + NDA
        </h3>
        <div className="flex gap-2">
          <button onClick={emailLink} disabled={busy}
                  data-testid="email-agreement-btn"
                  title="Generate a new signing link AND email it to the influencer"
                  className="px-3 py-1.5 text-xs bg-[#00F0FF] text-black border border-[#00F0FF] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Email signing link
          </button>
          <button onClick={generate} disabled={busy}
                  data-testid="generate-agreement-btn"
                  title="Generate a new signing link and copy it to clipboard (no email)"
                  className="px-3 py-1.5 text-xs bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/25 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
            Copy link only
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-neutral-500">Loading…</p>
      ) : signed ? (
        <div className="flex items-center gap-2 text-emerald-400 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          Signed by <b>{signed.signed_name}</b> on{' '}
          {new Date(signed.signed_at).toLocaleDateString()} · version {signed.version}
        </div>
      ) : unsignedLatest ? (
        <div className="space-y-1.5">
          <p className="text-xs text-amber-400">Awaiting signature.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-[#00F0FF] bg-black/40 px-2 py-1.5 rounded overflow-x-auto truncate">
              {unsignedLatest.sign_url}
            </code>
            <button onClick={() => { navigator.clipboard.writeText(unsignedLatest.sign_url); toast.success('Link copied'); }}
                    className="p-1.5 bg-white/5 hover:bg-white/10 rounded">
              <Copy className="w-3.5 h-3.5 text-neutral-400" />
            </button>
            <a href={unsignedLatest.sign_url} target="_blank" rel="noreferrer"
               className="p-1.5 bg-white/5 hover:bg-white/10 rounded">
              <ExternalLink className="w-3.5 h-3.5 text-neutral-400" />
            </a>
          </div>
        </div>
      ) : (
        <p className="text-xs text-neutral-500">No agreement issued yet.</p>
      )}
    </div>
  );
}

function AdminActionsBar({ affiliateId, inf, onClose, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: inf.name || '', slug: inf.slug || '',
    commission_rate: inf.commission_rate ?? 1.0,
    status: inf.status || 'active',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/admin/influencers/${affiliateId}`, {
        name: form.name,
        slug: form.slug.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
        commission_rate: parseFloat(form.commission_rate),
        status: form.status,
      }, { headers: auth() });
      toast.success('Updated');
      setEditing(false);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (hard) => {
    const verb = hard ? 'HARD DELETE (purges unpaid commissions + clicks)' : 'soft delete';
    if (!window.confirm(`${verb} this affiliate? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/admin/influencers/${affiliateId}${hard ? '?hard=true' : ''}`, { headers: auth() });
      toast.success(`Affiliate ${hard ? 'hard-deleted' : 'soft-deleted'}`);
      onChange?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4 mb-4 space-y-3">
        <h3 className="text-sm font-bold">Edit affiliate</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
          </Field>
          <Field label="Slug">
            <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-[#00F0FF] font-mono text-sm" />
          </Field>
          <Field label="Commission rate (%)">
            <input type="number" step="0.1" min="0" max="50" value={form.commission_rate}
                   onChange={(e) => setForm((f) => ({ ...f, commission_rate: e.target.value }))}
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
              <option value="active">active</option>
              <option value="invited">invited</option>
              <option value="deactivated">deactivated</option>
            </select>
          </Field>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} data-testid="save-edit-btn"
                  className="px-3 py-1.5 text-xs bg-[#00F0FF] text-black font-semibold rounded-lg disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 text-neutral-300 rounded-lg">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <button onClick={() => setEditing(true)} data-testid="edit-inf-btn"
              className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 text-neutral-300 rounded-lg inline-flex items-center gap-1.5">
        <Pencil className="w-3.5 h-3.5" /> Edit
      </button>
      <button onClick={() => remove(false)} disabled={busy} data-testid="soft-delete-btn"
              className="px-3 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
        <Trash2 className="w-3.5 h-3.5" /> Delete (soft)
      </button>
      <button onClick={() => remove(true)} disabled={busy} data-testid="hard-delete-btn"
              className="px-3 py-1.5 text-xs bg-red-500/30 hover:bg-red-500/50 text-red-200 border border-red-500/50 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
        <Trash2 className="w-3.5 h-3.5" /> Hard delete
      </button>
    </div>
  );
}

function AnalyticsSection({ affiliateId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4 mb-4" data-testid="analytics-section">
      <button onClick={() => setOpen((v) => !v)} data-testid="toggle-analytics-btn"
              className="w-full flex items-center justify-between text-left">
        <h3 className="text-sm font-bold inline-flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#00F0FF]" /> Private analytics
          <span className="text-[10px] uppercase tracking-wide text-neutral-500 font-normal">(admin + this affiliate only)</span>
        </h3>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
      </button>
      {open && (
        <div className="mt-4">
          <FounderAffiliateAnalytics
            endpoint={`${API}/api/admin/influencers/${affiliateId}/analytics`}
            headers={auth()}
          />
        </div>
      )}
    </div>
  );
}
